import { Stack } from '@fluentui/react';
import { EModelContext } from 'global/Compare/ComparisonEngine';
import { CascadeChildMergeActions, EMergeVMStatus, getAvailableMergeOptionsAsDropDown, IEntityVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IComment } from 'model/FlexibleAttributeModel';
import React, { useMemo, useState } from 'react';
import { ICompareConfiguration } from '../CompareLookupDialog/CompareLookupDialog';
import { getDefaultCollapsed, IMarkerSet, STACK_DEFAULT_PROPS } from './Compare.constants';
import { TSetCommentInfoDelegate } from './CompareContext';
import CompareMergeStatusIndicator from './CompareMergeStatusIndicator';
import { CompareStatusIndicator } from './CompareStatusIndicator';
import { CompareTitle } from './CompareTitle';
import CustomPanel, { getPanelStyleTypeFromMergeAction } from './CustomPanel';
import EntityFlexibleAttributePanel from './EntityFlexibleAttributePanel';
import EntityPropertiesPanel from './EntityPropertiesPanel';
import EntityReferencesPanel from './EntityReferencePanel';
import EntityTiersPanel from './EntityTiersPanel';
import MergeControl from './MergeControl';
import { TAuthenticationContext } from 'global/Authentication';


export interface ICompareEntityLocationContext {
    planId?: string;
    benefitId?: string;
    feesAndPremiumId?: string;
    claimsARId?: string;
    stopLossId?: string;
    groupNumberId?: string;
    nonStandardId?: string;
}

export interface IEntityComparePanelProps {
    compareVM: IEntityVM;
    stickyOffset: number;
    configuration: ICompareConfiguration;
    classNames: string;
    markers: IMarkerSet;
    onSetCommentInfo?: TSetCommentInfoDelegate;
    isMergeApp?: boolean;
    isUnderwritingApp?: boolean;
    comments?: Record<string, IComment>;
    mergeDisabled?: boolean;
    authentication: TAuthenticationContext;
    isPharmacyApp?: boolean;
}

const EntityComparePanel: React.FC<IEntityComparePanelProps> = (props) => {

    const stickyOffset = props.stickyOffset ?? 0;

    const mergeOptions = useMemo(
        () => {

            // Check if the entity is a group configuration by looking at the last context item
            // and comparing its name to ECompareContext.groupConfiguration
            const isForGroupConfiguration = props.compareVM.entityCompared.context &&
                props.compareVM.entityCompared.context.length > 0 &&
                props.compareVM.entityCompared.context[props.compareVM.entityCompared.context.length - 1].name === EModelContext.groupConfiguration;

            const options = getAvailableMergeOptionsAsDropDown(
                props.compareVM.compareResult,
                {
                    isGroupConfiguration: isForGroupConfiguration,
                    preventMerge: props.compareVM.tiersVM?.changed,
                }
            );

            return options;
        },
        [
            props.compareVM.compareResult,
            props.compareVM.tiersVM?.changed,
        ]
    );

    const [_, setMergeAction] = useState<EMergeVMStatus>(props.compareVM.mergeAction);

    const styleType = getPanelStyleTypeFromMergeAction(
        props.isMergeApp,
        props.compareVM.mergeAction
    );

    return (
        <CustomPanel
            title={<CompareTitle
                changed={props.compareVM.changed}
                name={props.compareVM.friendlyName ?? '?'}
                entityProps={props}
            />}
            headerRight={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CompareMergeStatusIndicator
                        mergeStatus={props.compareVM.mergeAction}
                        compareStatus={props.compareVM.compareResult}
                        isMergeApp={props.isMergeApp}
                    />
                    <CompareStatusIndicator
                        compareStatus={props.compareVM.compareResult}
                    />
                </div>
            }
            canCollapse={true}
            defaultCollapsed={getDefaultCollapsed(props.configuration, props.compareVM.changed)}
            id={props.compareVM.uniqueId}
            stickyHeader={true}
            stickyTop={stickyOffset}
            stickyZIndex={100}
            styleType={styleType}
            data-category-id={props.compareVM.id}
        >
            <Stack {...STACK_DEFAULT_PROPS}>
                {props.isMergeApp && !props.mergeDisabled && (
                    <MergeControl
                        selectedMergeOption={props.compareVM.mergeAction}
                        mergeOptions={mergeOptions}
                        entityProps={props}
                        entityVM={props.compareVM}
                        onChange={(event, option) => {
                            if (!option || option.data === undefined)
                                return;

                            CascadeChildMergeActions(
                                option.data,
                                props.configuration.defaultMergeAction,
                                props.compareVM,
                                null,
                                null,
                            );

                            setMergeAction(props.compareVM.mergeAction);
                        }}
                    />
                )}
                <EntityPropertiesPanel
                    entityProps={props}
                />
                <EntityTiersPanel
                    entityProps={props}
                />
                <EntityReferencesPanel
                    entityProps={props}
                />
                <EntityFlexibleAttributePanel
                    entityProps={props}
                    stickyOffset={stickyOffset}
                />
            </Stack>
        </CustomPanel >
    );
};

export default EntityComparePanel;

